import {css, cx as addStyles} from '@emotion/css';
import React, {HTMLAttributes, useState} from 'react';
import {Button} from './Button';
import {calculateGiftCardCashoutValue, formatValueToDollar, theme} from './Common';
import {Modal} from './Modal';
import {Span} from './Span';
import {toastr} from './Toastr';

export type CustomerPortalModalGiftCardDetailsProps = {
  isOpen: boolean;
  setIsOpen: (value: boolean) => unknown;
  value: number;
  email: string;
  cashOut: () => unknown;
  disabled: boolean;
};

export const CustomerPortalModalGiftCardDetails = ({
  isOpen,
  setIsOpen,
  value,
  email,
  cashOut,
  disabled,
}: CustomerPortalModalGiftCardDetailsProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [cashOutSuccess, setCashOutSuccess] = useState(false);

  const onCashOut = async () => {
    setIsLoading(true);
    try {
      await cashOut();
      setCashOutSuccess(true);
    } catch (_) {
      toastr().error('An error occurred while cashing out the gift card.');
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Modal title='Gift Card Details' isOpen={isOpen} setModal={setIsOpen}>
      <Content>
        {!cashOutSuccess ? (
          <>
            <Title>Confirm Gift Card Cashout</Title>
            <InfoContainer>
              <InfoWrapper>
                <Span fontColor='gray' fontSize='small' fontWeight='normal'>
                  Amount
                </Span>
                <Value>
                  {formatValueToDollar(calculateGiftCardCashoutValue(value), {
                    maximumFractionDigits: 2,
                  })}
                </Value>
                <Span fontColor='gray' fontSize='small' fontWeight='normal'>
                  Email
                </Span>
                <InfoValue>{email}</InfoValue>
              </InfoWrapper>
            </InfoContainer>
            <ButtonContainer>
              <Button buttonStyle='primary' onClick={onCashOut} disabled={disabled || isLoading}>
                Confirm
              </Button>
            </ButtonContainer>
          </>
        ) : (
          <>
            <InfoContainer>
              <InfoWrapper>
                <div
                  className={css({
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    maxWidth: '390px',
                  })}
                >
                  <Span fontColor='darkGray' fontSize='medium' fontWeight='normal'>
                    Done! Your Reperch Gift Card is on its way to{' '}
                    <Span fontColor='darkGray' fontSize='medium' fontWeight='bold'>
                      {email}
                    </Span>
                    .
                    <br />
                    Happy treasure hunting!
                  </Span>
                  <Span
                    fontColor='darkGray'
                    fontWeight='light'
                    fontSize='small'
                    style={{marginTop: theme.spacings.medium}}
                  >
                    Gift Card amount:{' '}
                    {formatValueToDollar(calculateGiftCardCashoutValue(value), {
                      maximumFractionDigits: 2,
                    })}
                  </Span>
                </div>
              </InfoWrapper>
            </InfoContainer>
          </>
        )}
      </Content>
    </Modal>
  );
};
const flexStartColumn = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
});
const Content = (args: HTMLAttributes<HTMLDivElement>) => (
  <div className={flexStartColumn} {...args} />
);
const textBase = css({fontFamily: theme.font.family, textAlign: 'left'});
const Title = (args: HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={addStyles(
      textBase,
      css({
        color: theme.colors.darkGray,
        fontWeight: theme.font.medium,
        fontSize: theme.font.sizes.medium,
      }),
    )}
    {...args}
  />
);
const Value = (args: HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={addStyles(
      textBase,
      css({
        color: theme.colors.darkBlue,
        fontWeight: theme.font.medium,
        fontSize: theme.font.sizes.medium,
        marginTop: theme.spacings.xxsmall,
        marginBottom: theme.spacings.small,
      }),
    )}
    {...args}
  />
);
const InfoContainer = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      flexStartColumn,
      css({
        [theme.mediaMaxWidth.small]: {marginTop: theme.spacings.xsmall},
      }),
    )}
    {...args}
  />
);
const InfoWrapper = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      css({
        marginTop: theme.spacings.small,
      }),
    )}
    {...args}
  />
);
const InfoValue = (args: HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={addStyles(
      textBase,
      css({
        color: theme.colors.darkGray,
        fontWeight: theme.font.normal,
        fontSize: theme.font.sizes.medium,
        marginTop: theme.spacings.xxsmall,
      }),
    )}
    {...args}
  />
);
const centeredAlignedRow = css({display: 'flex', flexDirection: 'row', alignItems: 'center'});
const ButtonContainer = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      centeredAlignedRow,
      css({
        justifyContent: 'center',
        width: '100%',
        marginTop: theme.spacings.large,
        [theme.mediaMaxWidth.small]: {marginTop: theme.spacings.medium},
      }),
    )}
    {...args}
  />
);
